import { environment } from 'src/environments/environment';
import { JwtHelperService } from './jwtHelper.service';

/**
 * Sesión central de navegador de PIVI (SSO entre canales).
 *
 * El login de siempre (front → backend propio → PIVI) no cambia. Esto sólo habla
 * **directo** con `{piviBaseUrl}/browser/v1/session` para crear, canjear y cerrar la
 * cookie `PIVI_SESSION`. Contrato: PIVI-BROWSER-SESSION.md en el repo de PIVI.
 *
 * Se usa `fetch` y no `HttpClient` a propósito: el `authInterceptor` le pondría el
 * Bearer del canal al canje (PIVI respondería 401 antes de mirar la cookie) y ante
 * cualquier 401 de PIVI intentaría refrescar o haría `logout()`. Estas llamadas no
 * deben pasar por él.
 *
 * Reglas del contrato:
 *  - `credentials: 'include'` en todas: sin eso el navegador no manda ni guarda la
 *    cookie. La cookie es HttpOnly: nunca se lee ni se guarda desde aquí.
 *  - Nada de esto puede bloquear al usuario: 2 s de tope y cualquier fallo se trata
 *    como "no hay SSO".
 */

const TIMEOUT_MS = 2000;
const RUTA_SESION = '/browser/v1/session';
const CABECERA_CLIENTE = { 'X-PIVI-Client': '1' };

/** Qué hacer tras intentar canjear la sesión central al arrancar. */
export type ResultadoCanje =
  /** Había sesión central y ya se guardaron token y refresh del canal. */
  | 'autenticado'
  /** Sin sesión, sin MFA suficiente, error o timeout: login normal, en silencio. */
  | 'login'
  /** Sesión válida, pero el usuario no pertenece a este canal. No mandar al login. */
  | 'sin-acceso';

interface TokensCanal {
  token: string;
  refreshToken: string;
}

/**
 * URL base de PIVI, o `null` si el SSO está apagado (vacía, mal formada, o sin HTTPS
 * en producción). Se lee en cada llamada porque `env.json` puede sobrescribirla al
 * arrancar el contenedor.
 */
function urlBasePivi(): string | null {
  const cruda = environment.piviBaseUrl?.trim();
  if (!cruda) return null;

  let url: URL;
  try {
    url = new URL(cruda);
  } catch {
    console.error(`[pivi] piviBaseUrl no es una URL válida: "${cruda}"`);
    return null;
  }

  if (environment.production && url.protocol !== 'https:') {
    console.error('[pivi] piviBaseUrl debe ser HTTPS en producción');
    return null;
  }

  return url.origin + url.pathname.replace(/\/+$/, '');
}

/**
 * POST /browser/v1/session — crea o renueva la sesión central tras un login completo.
 *
 * Fire-and-forget: no devuelve nada que esperar y nunca lanza. Si falla, el usuario
 * simplemente no tendrá SSO en esta sesión.
 */
export function crearSesionCentral(token: string | null): void {
  const base = urlBasePivi();
  if (!base || !token) return;

  fetch(`${base}${RUTA_SESION}`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...CABECERA_CLIENTE, Authorization: `Bearer ${token}` },
    signal: AbortSignal.timeout(TIMEOUT_MS),
  })
    .then((respuesta) => {
      if (!respuesta.ok) {
        console.warn(
          `[pivi] no se creó la sesión central (HTTP ${respuesta.status})`,
        );
      }
    })
    .catch((error: unknown) =>
      console.warn('[pivi] no se creó la sesión central', error),
    );
}

/**
 * DELETE /browser/v1/session — logout global. Resuelve siempre (éxito, error o
 * timeout): quien lo espera tiene que poder seguir con la limpieza local.
 */
export async function cerrarSesionCentral(): Promise<void> {
  const base = urlBasePivi();
  if (!base) return;

  try {
    const respuesta = await fetch(`${base}${RUTA_SESION}`, {
      method: 'DELETE',
      credentials: 'include',
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
    if (!respuesta.ok) {
      console.warn(
        `[pivi] no se cerró la sesión central (HTTP ${respuesta.status})`,
      );
    }
  } catch (error) {
    console.warn('[pivi] no se cerró la sesión central', error);
  }
}

/**
 * POST /browser/v1/session/token — canjea la sesión central por tokens del canal.
 *
 * Sin `Authorization`: un Bearer vencido haría que PIVI respondiera 401 sin mirar la
 * cookie. El caso se decide por el `codigo` del cuerpo, no sólo por el status.
 */
async function canjearSesionCentral(
  base: string,
): Promise<
  { tipo: 'tokens'; tokens: TokensCanal } | { tipo: 'sin-acceso' | 'login' }
> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`${base}${RUTA_SESION}/token`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...CABECERA_CLIENTE, 'Content-Type': 'application/json' },
      body: JSON.stringify({ channelId: environment.piviChannelId }),
      signal: AbortSignal.timeout(TIMEOUT_MS),
    });
  } catch (error) {
    // Red caída, CORS (origen no registrado en el canal) o timeout.
    console.warn('[pivi] canje de sesión central no disponible', error);
    return { tipo: 'login' };
  }

  const cuerpo = await leerJson(respuesta);

  if (respuesta.ok) {
    const token = cuerpo?.['token'];
    const refreshToken = cuerpo?.['refreshToken'];
    if (typeof token === 'string' && typeof refreshToken === 'string') {
      return { tipo: 'tokens', tokens: { token, refreshToken } };
    }
    console.warn('[pivi] el canje respondió sin token o refreshToken');
    return { tipo: 'login' };
  }

  const codigo = cuerpo?.['codigo'];

  // Único caso que NO va al login: con su clave correcta el usuario volvería aquí
  // y entraría en un bucle.
  if (respuesta.status === 403 && codigo === 'SIN_ACCESO_CANAL') {
    return { tipo: 'sin-acceso' };
  }

  // SESION_INVALIDA (lo normal: no hay sesión central) y MFA_REQUERIDA (el canal
  // exige MFA y la sesión no la tiene) van al login sin ruido. El resto delata una
  // configuración rota (origen, cabecera) o rate limit: login igual, pero se registra.
  if (codigo !== 'SESION_INVALIDA' && codigo !== 'MFA_REQUERIDA') {
    console.warn(
      `[pivi] canje rechazado (HTTP ${respuesta.status}${codigo ? `, ${codigo}` : ''})`,
    );
  }
  return { tipo: 'login' };
}

async function leerJson(
  respuesta: Response,
): Promise<Record<string, unknown> | null> {
  try {
    const cuerpo: unknown = await respuesta.json();
    return cuerpo && typeof cuerpo === 'object'
      ? (cuerpo as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Rutas donde no se intenta el canje. */
function debeOmitirCanje(): boolean {
  const { pathname, search } = window.location;

  // Página pública con su propio JWT en la URL.
  if (pathname.startsWith('/recaudos/')) return true;
  // A mitad de un login MFA: el usuario ya está en el flujo normal.
  if (pathname.startsWith('/authentication/mfa-')) return true;

  // Retorno de un login SSO/OIDC en curso: que lo termine LoginPageComponent.
  const params = new URLSearchParams(search);
  return ['code', 'sso_code', 'sso_error', 'error'].some((k) => params.has(k));
}

let resultadoArranque: ResultadoCanje = 'login';

/** Lo que resolvió `intentarSsoAlArrancar()`. */
export function resultadoCanjeArranque(): ResultadoCanje {
  return resultadoArranque;
}

/**
 * Paso previo a la autenticación: si no hay token local, intenta entrar con la
 * sesión central de PIVI.
 *
 * Corre en `main.ts` **antes** de arrancar Angular, no en `isAuthenticatedGuard`:
 * en cuanto se crea `AuthService`, `checkStatus()` sin token llama a `logout()` y
 * navega al login, lo que cancelaría la navegación del guard a mitad del canje.
 * Resolviéndolo antes, `AuthService` nace ya con el token en localStorage y todo
 * sigue como si el login hubiera ocurrido (incluido el `/api/v1/status` contra el
 * backend propio y el guard, que ve el token y deja pasar).
 *
 * Nunca lanza y como mucho tarda `TIMEOUT_MS`.
 */
export async function intentarSsoAlArrancar(): Promise<void> {
  if (localStorage.getItem('token')) return;

  const base = urlBasePivi();
  if (!base || debeOmitirCanje()) return;

  const resultado = await canjearSesionCentral(base);

  if (resultado.tipo === 'tokens') {
    if (guardarTokens(resultado.tokens)) resultadoArranque = 'autenticado';
    return;
  }

  resultadoArranque = resultado.tipo;
}

/**
 * Guarda los tokens exactamente donde lo hace el login normal (`AuthService.login`
 * + `handleAuthSuccess`): `token`, `refreshToken` y el `user` sacado del JWT.
 */
function guardarTokens({ token, refreshToken }: TokensCanal): boolean {
  const jwtHelper = new JwtHelperService();

  // Sin el `user` la app no sabría quién es ni sus roles: mejor el login normal
  // que una sesión a medias.
  if (!jwtHelper.saveUserToLocalStorage(token)) return false;

  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken);
  return true;
}
